/**
 * Auto Arrow Generation Utility
 * Creates arrows between adjacent signs on the same line
 */

import type { PlacedSign, LatLng } from '../types/signs';
import type { SignLine } from '../types/signLines';
import type { ArrowDescriptor } from '../types/arrows';
import { haversineDistance } from './signLineDetection';

/**
 * Arrow endpoints are positioned directly AT the sign dot positions
 * Both arrow heads of the double-headed arrow sit under their respective sign dots
 */
const calculateArrowEndpoint = (
  signPosition: LatLng,
  _nextSignPosition: LatLng,
  _isStart: boolean,
  _signScale: number = 1.0
): LatLng => {
  // Position arrow endpoint directly at the sign dot position
  // Both arrow heads will be under their respective sign dots
  return {
    lat: signPosition.lat,
    lng: signPosition.lng,
  };
};

/**
 * Generate an arrow between two adjacent signs
 */
export const generateArrowBetweenSigns = (
  sign1: PlacedSign,
  sign2: PlacedSign,
  signLineId: string
): ArrowDescriptor => {
  // Calculate arrow start (bottom edge of sign1 toward sign2)
  const start = calculateArrowEndpoint(
    { lat: sign1.lat, lng: sign1.lng },
    { lat: sign2.lat, lng: sign2.lng },
    true,
    sign1.scale || 1.0
  );

  // Calculate arrow end (top edge of sign2 from sign1)
  const end = calculateArrowEndpoint(
    { lat: sign2.lat, lng: sign2.lng },
    { lat: sign1.lat, lng: sign1.lng },
    false,
    sign2.scale || 1.0
  );

  // Calculate actual distance between the arrow endpoints
  const distance = haversineDistance(start.lat, start.lng, end.lat, end.lng);

  return {
    id: `auto-arrow-${sign1.id}-${sign2.id}-${Date.now()}`,
    start,
    end,
    distance: Math.round(distance * 10) / 10, // Round to 1 decimal place
    showDistanceLabel: true,
    startSignId: sign1.id,
    endSignId: sign2.id,
    isAutoGenerated: true,
    signLineId,
  };
};

/**
 * Generate all arrows for a sign line
 * Creates arrows between each adjacent pair of signs
 */
export const generateArrowsForSignLine = (
  signLine: SignLine,
  allSigns: PlacedSign[]
): ArrowDescriptor[] => {
  if (!signLine.autoArrowsEnabled || signLine.signIds.length < 2) {
    return [];
  }

  const signMap = new Map(allSigns.map(s => [s.id, s]));
  const arrows: ArrowDescriptor[] = [];

  // Generate arrow between each adjacent pair
  for (let i = 0; i < signLine.signIds.length - 1; i++) {
    const sign1 = signMap.get(signLine.signIds[i]);
    const sign2 = signMap.get(signLine.signIds[i + 1]);

    if (sign1 && sign2) {
      arrows.push(generateArrowBetweenSigns(sign1, sign2, signLine.id));
    }
  }

  return arrows;
};

/**
 * Generate all auto arrows for multiple sign lines
 */
export const generateAllAutoArrows = (
  signLines: SignLine[],
  allSigns: PlacedSign[]
): ArrowDescriptor[] => {
  return signLines.flatMap(line => generateArrowsForSignLine(line, allSigns));
};

/**
 * Update auto arrows when a sign is moved
 * Returns updated arrows that involve the moved sign
 */
export const updateArrowsForMovedSign = (
  movedSignId: string,
  newPosition: LatLng,
  existingArrows: ArrowDescriptor[],
  allSigns: PlacedSign[]
): ArrowDescriptor[] => {
  // Find arrows connected to the moved sign
  const affectedArrows = existingArrows.filter(
    arrow => arrow.isAutoGenerated && 
    (arrow.startSignId === movedSignId || arrow.endSignId === movedSignId)
  );

  const signMap = new Map(allSigns.map(s => [s.id, s]));
  
  // Update the moved sign's position in our local map
  const movedSign = signMap.get(movedSignId);
  if (movedSign) {
    signMap.set(movedSignId, { ...movedSign, lat: newPosition.lat, lng: newPosition.lng });
  }

  // Regenerate affected arrows
  const updatedArrows = affectedArrows.map(arrow => {
    const sign1 = signMap.get(arrow.startSignId!);
    const sign2 = signMap.get(arrow.endSignId!);

    if (!sign1 || !sign2) {
      return arrow;
    }

    // Recalculate arrow endpoints and distance
    const start = calculateArrowEndpoint(
      { lat: sign1.lat, lng: sign1.lng },
      { lat: sign2.lat, lng: sign2.lng },
      true,
      sign1.scale || 1.0
    );

    const end = calculateArrowEndpoint(
      { lat: sign2.lat, lng: sign2.lng },
      { lat: sign1.lat, lng: sign1.lng },
      false,
      sign2.scale || 1.0
    );

    const distance = haversineDistance(start.lat, start.lng, end.lat, end.lng);

    return {
      ...arrow,
      start,
      end,
      distance: Math.round(distance * 10) / 10,
    };
  });

  // Merge updated arrows back into the full list
  const updatedArrowIds = new Set(updatedArrows.map(a => a.id));
  return [
    ...existingArrows.filter(a => !updatedArrowIds.has(a.id)),
    ...updatedArrows,
  ];
};

/**
 * Remove auto arrows associated with a deleted sign
 */
export const removeArrowsForDeletedSign = (
  deletedSignId: string,
  existingArrows: ArrowDescriptor[]
): ArrowDescriptor[] => {
  return existingArrows.filter(
    arrow => !arrow.isAutoGenerated || 
    (arrow.startSignId !== deletedSignId && arrow.endSignId !== deletedSignId)
  );
};

/**
 * Remove all auto arrows for a specific sign line
 */
export const removeArrowsForSignLine = (
  signLineId: string,
  existingArrows: ArrowDescriptor[]
): ArrowDescriptor[] => {
  return existingArrows.filter(
    arrow => !arrow.isAutoGenerated || arrow.signLineId !== signLineId
  );
};

/**
 * Sync auto arrows with current sign lines
 * Removes stale arrows and adds new ones as needed
 */
export const syncAutoArrows = (
  signLines: SignLine[],
  allSigns: PlacedSign[],
  existingArrows: ArrowDescriptor[]
): ArrowDescriptor[] => {
  // Separate manual and auto arrows
  const manualArrows = existingArrows.filter(a => !a.isAutoGenerated);

  const makeAutoKey = (arrow: ArrowDescriptor) => {
    const a = arrow.startSignId ?? '';
    const b = arrow.endSignId ?? '';
    const [first, second] = a < b ? [a, b] : [b, a];
    return `${first}|${second}`;
  };

  const existingAutoByKey = new Map(
    existingArrows
      .filter(a => a.isAutoGenerated)
      .map(a => [makeAutoKey(a), a])
  );
  
  // Regenerate all auto arrows based on current sign lines
  const newAutoArrows = generateAllAutoArrows(signLines, allSigns).map((arrow) => {
    const existing = existingAutoByKey.get(makeAutoKey(arrow));
    return {
      ...arrow,
      showDistanceLabel: existing?.showDistanceLabel ?? arrow.showDistanceLabel ?? true,
    };
  });

  return [...manualArrows, ...newAutoArrows];
};
